import {
    EventSourceMessage,
    FetchEventSourceInit,
} from '@microsoft/fetch-event-source';
import { getConfig } from '@superbet-group/web.lib.config';
import {
    Country,
    Site,
} from '@superbet-group/web.types';

const meticulousEventListeners = new Set<string>();

export function isMeticulousContext() {
    return process.env.IS_METICULOUS === 'true';
}

export function getMeticulousSite() {
    if (!isMeticulousContext()) {
        return Site.SuperbetRo; // Should never happen
    }

    switch (getConfig().countrySpecific.country) {
        case Country.Ro:
            return Site.SuperbetRo;
        case Country.Pl:
            return Site.SuperbetPl;
        case Country.Br:
            return Site.SuperbetBr;
        case Country.Bg:
            return Site.SuperbetBg;
        case Country.Rs:
            return Site.SuperbetRs;
        case Country.RoNapoleon:
            return Site.NapoleonGamesRo;
        case Country.BrSocial:
            return Site.SuperSocialBr;
        case Country.Com:
            return Site.SuperbetCom;
        case Country.Be:
            return Site.NapoleonSports; // TODO: Figure this one out when gaming is included
        default:
            return Site.SuperbetRo;
    }
}

export function stopMeticulousRecording() {
    if ('__meticulous' in window) {
        window.__meticulous?.stopRecording?.();
    }
}

export const MeticulousSseUtils = {
    recordMeticulousSseEvent,
    addMeticulousSseEventListeners,
};

export const MeticulousTargetSupportUtil = {
    recordTargetCountry,
    retrieveTargetCountry,
    isRunningInRecordedCountry,
};

function recordTargetCountry(country: Country): void {
    if (!window.Meticulous || window.Meticulous.isRunningAsTest) {
        return;
    }

    window.Meticulous?.record?.recordCustomData('targetCountry', country);
}

function retrieveTargetCountry(): Country | undefined {
    if (!window.Meticulous?.isRunningAsTest) {
        return;
    }

    const storedCountry = window.Meticulous!.replay?.retrieveCustomData('targetCountry') as Country;

    return storedCountry ?? Country.Ro; // If a test was recorded without storing this, it was recorded in Romania
}

function isRunningInRecordedCountry() {
    const recordedCountry = retrieveTargetCountry();

    if (recordedCountry == null) {
        return false;
    }

    return getConfig().countrySpecific.country === recordedCountry;
}

function recordMeticulousSseEvent<Event extends MeticulousSseEvent>(endpoint: string, type: Event['type'], data: Event['data']): void {
    // We only record events when running in Meticulous recording environments (e.g., local development, or testing on Stage)
    if (!window.Meticulous || window.Meticulous.isRunningAsTest) {
        return;
    }

    window.Meticulous.record?.recordCustomEvent(`event-streaming-${endpoint}`, JSON.stringify({ type, data }));
}

function addMeticulousSseEventListeners(
    endpoint: string,
    fetchEventSourceInit: FetchEventSourceInit,
): void {
    if (meticulousEventListeners.has(endpoint)) {
        return;
    }

    meticulousEventListeners.add(endpoint);

    window.Meticulous?.replay?.addCustomEventListener(`event-streaming-${endpoint}`, processMeticulousCustomEvent(fetchEventSourceInit));
}

function processMeticulousCustomEvent(fetchEventSourceInit: FetchEventSourceInit) {
    return (event: string) => {
        const parsed = JSON.parse(event) as MeticulousSseEvent;

        if (parsed.type in fetchEventSourceInit) {
            try {
                fetchEventSourceInit[parsed.type]?.(parsed.data);
            } catch (error) {
                console.error(error); // Logger not necessary, this only runs in Meticulous envs
            }
        }
    };
}

export type MeticulousSseOpenEvent = {
    type: 'onopen';
    data: Response;
};

export type MeticulousSseMessageEvent = {
    type: 'onmessage';
    data: EventSourceMessage;
};

export type MeticulousSseCloseEvent = {
    type: 'onclose';
    data: void;
};

export type MeticulousSseErrorEvent = {
    type: 'onerror';
    data: any;
};

export type MeticulousSseEvent =
    | MeticulousSseOpenEvent
    | MeticulousSseMessageEvent
    | MeticulousSseCloseEvent
    | MeticulousSseErrorEvent;
